import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Card, CardContent, CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu, MenuItem,
  TextField, Tooltip,
  Typography
} from '@mui/material';
import {
  Building,
  FileText, Mic,
  Play,
  StickyNote,
  Video
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import apiInstance from 'src/utils/apiAuth';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import * as constants from 'src/utils/constants';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import { createVideoThumbnail } from 'src/utils/videoThumbnailUtil';
import { TextNoteDialog } from '../journey/EditJourney';

// Learning Card Component - supports both videos and documents
function LearningContentCard({ learningItem, onEdit, onRemove, onViewInfo }) {
  const [open, setOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);

  const document = learningItem.document || {};
  const company = learningItem.company || {};
  const addedBy = learningItem.added_by || {};

  const fileUrl = document.file || '';
  const fileTitle = document.file_name || document.title || 'Content';

  // Determine file type
  const getFileType = () => {
    if (!fileUrl) return 'document';
    const extension = fileUrl.split('.').pop().toLowerCase();
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(extension)) return 'audio';
    if (['mp4', 'avi', 'mov', 'webm', 'mkv', '3gp', 'flv'].includes(extension)) return 'video';
    return 'document';
  };

  const fileType = getFileType();

  useEffect(() => {
    const generate = async () => {
      try {
        if (!fileUrl || fileType !== 'video') return;
        const src = fileUrl.startsWith('http')
          ? fileUrl
          : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`;
        const img = await createVideoThumbnail(src);
        if (img) setThumbnail(img);
      } catch (e) {
        console.error('Thumbnail generation failed:', e.message);
      }
    };
    generate();
  }, [fileUrl, fileType]);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setMenuAnchorEl(null);
  };

  return (
    <>
      <Card
        onClick={() => setOpen(true)}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          },
          borderRadius: 3,
          overflow: 'hidden',
          height: '100%'
        }}
      >
        <Box sx={{
          height: 140,
          backgroundColor: fileType === 'video' ? '#000' : fileType === 'audio' ? '#fff3e0' : '#e8f4fd',
          backgroundImage: thumbnail ? `url(${thumbnail})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          {fileType === 'video' && !thumbnail && (
            <video
              src={fileUrl}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0
              }}
              muted
              playsInline
              preload="metadata"
            />
          )}
          {fileType === 'video' ? (
            <Play size={40} color="rgba(255,255,255,0.9)" style={{
              position: 'relative',
              zIndex: 1,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
            }} />
          ) : fileType === 'audio' ? (
            <Mic size={40} color="#ff9800" />
          ) : (
            <FileText size={40} color="#2196f3" />
          )}

          <Chip
            icon={<Building size={12} />}
            label={company.company_name || 'Unknown'}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(255,255,255,0.9)',
              fontSize: '0.65rem',
              height: 22,
              '& .MuiChip-icon': { fontSize: 12 }
            }}
          />

          <IconButton
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: '#e3f2fd' },
              width: 28,
              height: 28
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16, color: '#64748b' }} />
          </IconButton>
        </Box>

        <CardContent sx={{ p: 1.5 }}>
          <Tooltip title={fileTitle} arrow>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.8rem'
              }}
            >
              {fileTitle}
            </Typography>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Avatar
              sx={{ width: 18, height: 18, fontSize: '0.6rem', bgcolor: '#1976d2' }}
            >
              {addedBy.first_name?.[0] || 'U'}
            </Avatar>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              Added by {addedBy.first_name} {addedBy.last_name}
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            Added: {getDDMMYYYYFormat(learningItem.created)}
          </Typography>
        </CardContent>
      </Card>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
            minWidth: 180
          }
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
            onViewInfo(learningItem);
          }}
        >
          <ViewIcon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
          <Typography color="#1976d2">View Notes</Typography>
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
            onEdit(learningItem);
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: 18, color: '#ed6c02' }} />
          <Typography color="#ed6c02">Edit Notes</Typography>
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
            onRemove(learningItem);
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
          <Typography color="#d32f2f">Remove from Learning</Typography>
        </MenuItem>
      </Menu>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{fileTitle}</Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: fileType === 'video' ? 0 : 2 }}>
          {fileType === 'video' ? (
            <video
              controls
              autoPlay
              style={{ width: '100%', height: 'auto', maxHeight: '500px' }}
              src={fileUrl}
            >
              Your browser does not support the video tag.
            </video>
          ) : fileType === 'audio' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <Mic size={64} color="#ff9800" style={{ marginBottom: 16 }} />
              <audio controls style={{ width: '100%' }} src={fileUrl}>
                Your browser does not support the audio tag.
              </audio>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <FileText size={64} color="#2196f3" style={{ marginBottom: 16 }} />
              <Typography variant="body1" sx={{ mb: 2 }}>
                Document Preview
              </Typography>
              <Button
                variant="contained"
                href={fileUrl.startsWith('http') ? fileUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Document
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Main Learning Page Component
export default function Learning() {
  const [learningData, setLearningData] = useState([]);
  const [allStages, setAllStages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('stage');

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);

  const [recordingStages, setRecordingStages] = useState({});
  const [recordingTimes, setRecordingTimes] = useState({});
  const [mediaRecorders, setMediaRecorders] = useState({});
  const [textNoteDialogOpen, setTextNoteDialogOpen] = useState(false);
  const [textNoteTitle, setTextNoteTitle] = useState('');
  const [textNoteContent, setTextNoteContent] = useState('');
  const [textNoteStage, setTextNoteStage] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const recognitionRef = useRef(null);

  // Fetch all stages
  const fetchAllStages = async () => {
    try {
      const response = await apiInstance.get('tasks/stages/', {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      if (response.data) {
        // Filter out stages with 0% or 5%
        const filteredStages = (response.data || []).filter(stage => {
          const stageName = stage.name || '';
          return !(stageName.includes(' 0%') || stageName.includes(' 5%') ||
            stageName.startsWith('0%') || stageName.startsWith('5%'));
        });
        setAllStages(filteredStages);
        return filteredStages;
      }
      return [];
    } catch (error) {
      console.error('Error fetching stages:', error);
      return [];
    }
  };

  const fetchLearningData = async (stages = []) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('grouped', 'true');
      if (searchQuery) params.append('search', searchQuery);

      const response = await apiInstance.get(`learning/?${params.toString()}`, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });

      if (response.data?.success) {
        const learningByStage = response.data.data || [];
        const learningMap = {};
        let uncategorizedVideos = [];
        learningByStage.forEach(group => {
          if (group.stage?.id) {
            learningMap[group.stage.id] = group.videos || [];
          } else if (!group.stage) {
            uncategorizedVideos = group.videos || [];
          }
        });

        const stagesToUse = stages.length > 0 ? stages : allStages;
        const mergedData = stagesToUse.map(stage => ({
          stage: {
            id: stage.id,
            name: stage.name,
            label: stage.label,
            order: stage.order
          },
          videos: learningMap[stage.id] || [],
          count: (learningMap[stage.id] || []).length
        }));
        
        // Add uncategorized items (no stage) at the end
        if (uncategorizedVideos.length > 0) {
          mergedData.push({
            stage: { id: null, name: 'Uncategorized', label: 'Uncategorized', order: 9999 },
            videos: uncategorizedVideos,
            count: uncategorizedVideos.length
          });
        }
        
        // Sort by stage order
        mergedData.sort((a, b) => (a.stage.order || 0) - (b.stage.order || 0));
        setLearningData(mergedData);
      }
    } catch (error) {
      console.error('Error fetching learning data:', error);
      showErrorToast('Failed to load learning content');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const stages = await fetchAllStages();
      await fetchLearningData(stages);
    };
    init();
  }, []);

  useEffect(() => {
    if (allStages.length > 0) {
      const delaySearch = setTimeout(() => {
        fetchLearningData(allStages);
      }, 500);
      return () => clearTimeout(delaySearch);
    }
  }, [searchQuery]);

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setEditNotes(item.notes || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    setIsSaving(true);
    try {
      const response = await apiInstance.patch(`learning/${editingItem.id}/`, {
        notes: editNotes
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });

      if (response.data?.success) {
        showToast('Notes updated successfully', 'success');
        setEditDialogOpen(false);
        fetchLearningData();
      }
    } catch (error) {
      showErrorToast('Failed to update notes');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle remove
  const handleRemove = (item) => {
    setDeletingItem(item);
    setDeleteDialogOpen(true);
  };

  const confirmRemove = async () => {
    if (!deletingItem) return;
    setIsDeleting(true);
    try {
      const response = await apiInstance.delete(`learning/${deletingItem.id}/`, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      showToast('Video removed from Learning', 'success');
      setDeleteDialogOpen(false);
      setLearningData(prev => prev.map(data => {
        if (data.stage.id === deletingItem.stage.id) {
          const updatedVideos = data.videos.filter(video => video.id !== deletingItem.id)
          return { ...data, videos: updatedVideos, count: data.count - 1 }
        }
        return data
      }))
    } catch (error) {
      showErrorToast('Failed to remove video');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle view info
  const handleViewInfo = (item) => {
    setViewingItem(item);
    setInfoDialogOpen(true);
  };

  const totalContent = learningData.reduce((acc, group) => acc + (group.count || 0), 0);

  const uploadUniversalFile = async (file, stage) => {
    const stageKey = `stage-${stage.id}`;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);
      formData.append('stage_id', stage.stage.id);
      formData.append('stage_name', stage.stage.name);
      formData.append('uploaded_on', new Date().toISOString());

      const response = await apiInstance.post('learning/upload-document/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Token ${localStorage.getItem('authToken')}`
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      });

      showToast('File uploaded successfully', 'success');
      fetchLearningData(allStages)

    } catch (error) {
      showErrorToast('Error uploading file: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUniversalFileUpload = (event, stage) => {
    const file = event.target.files[0];
    if (!file) return;
    const acceptAttr = event.target.accept;
    const fileName = file.name.toLowerCase();

    if (acceptAttr.includes('video/*') || acceptAttr.includes('.mp4')) {
      const videoExtensions = ['.mp4', '.avi', '.mov', '.webm', '.mkv', '.flv', '.wmv', '.m4a'];
      if (!videoExtensions.some(ext => fileName.endsWith(ext))) {
        showErrorToast('Please upload only video files (MP4, AVI, MOV, WEBM, MKV, FLV, WMV, M4A)');
        event.target.value = '';
        return;
      }
    }

    if (acceptAttr.includes('.pdf') && !acceptAttr.includes('video')) {
      const docExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt', '.csv'];
      if (!docExtensions.some(ext => fileName.endsWith(ext))) {
        showErrorToast('Please upload only document files (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, CSV)');
        event.target.value = '';
        return;
      }
    }
    uploadUniversalFile(file, stage);
  };

  const uploadAudioFile = async (file, stage) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', `Audio Recording - ${stage.stage.name}`);
      formData.append('stage_id', stage.stage.id);
      formData.append('stage_name', stage.stage.name);

      const response = await apiInstance.post('learning/upload-document/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Token ${localStorage.getItem('authToken')}`
        }
      });

      showToast('Audio recording saved successfully', 'success');
      fetchLearningData(allStages)
      // setLearningData((prev) => prev.map((data) => {
      //   if (data.stage.id === response.data.data.stage_id) {
      //     return { ...data, videos: [response.data, ...data.videos] }
      //   }
      //   else {
      //     return data;
      //   }
      // }))
    } catch (error) {
      showErrorToast('Error saving audio recording');
    }
  };

  const startRecording = async (stage) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      const chunks = []
      const stageId = stage.stage.id

      recorder.ondataavailable = e => chunks.push(e.data)

      const timer = setInterval(() => {
        setRecordingTimes(prev => ({
          ...prev,
          [stageId]: (prev[stageId] || 0) + 1
        }))
      }, 1000)

      recorder.onstop = () => {
        clearInterval(timer)
        const blob = new Blob(chunks, { type: "audio/wav" })
        const file = new File([blob], `recording-${Date.now()}.wav`, {
          type: "audio/wav"
        })

        uploadAudioFile(file, stage)
        stream.getTracks().forEach(t => t.stop())

        setRecordingStages(prev => ({ ...prev, [stageId]: false }))
        setRecordingTimes(prev => ({ ...prev, [stageId]: 0 }))
        setMediaRecorders(prev => ({ ...prev, [stageId]: null }))
      }

      recorder.start()

      setMediaRecorders(prev => ({ ...prev, [stageId]: recorder }))
      setRecordingStages(prev => ({ ...prev, [stageId]: true }))
      setRecordingTimes(prev => ({ ...prev, [stageId]: 0 }))
    } catch {
      showErrorToast("Error accessing microphone")
    }
  }

  const stopRecording = (stage) => {
    const recorder = mediaRecorders[stage.stage.id];
    if (recorder && recordingStages[stage.stage.id]) {
      recorder.stop();
    }
  };

  const handleStartListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      showErrorToast(null, 'Speech recognition is not supported in your browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      showToast('Listening...', 'info', 1000);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        const currentText = (textNoteContent || '').replace(/<[^>]*>/g, '');
        const newText = currentText + (currentText ? ' ' : '') + finalTranscript;
        setTextNoteContent(newText);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      showErrorToast('Speech recognition error: ' + event.error);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleStopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      showToast('Stopped listening', 'info', 1000);
    }
  };

  const handleSaveTextNote = async () => {
    if (!textNoteContent || !textNoteContent.trim()) {
      showErrorToast('Please enter some text');
      return;
    }

    if (!textNoteStage) return;

    setIsSavingNote(true);
    try {
      // Create a text file
      const blob = new Blob([textNoteContent], { type: 'text/plain' });
      const title = textNoteTitle || `Note-${new Date().toLocaleDateString()}`;
      // Ensure filename ends in .txt
      const safeTitle = title.replace(/[^a-zA-Z0-9-_ ]/g, '');
      const fileName = `${safeTitle}.txt`;
      const file = new File([blob], fileName, { type: 'text/plain' });

      const mockStageGroup = {
        stage: {
          id: textNoteStage.stage.id,
          name: textNoteStage.stage.name
        }
      };

      await uploadUniversalFile(file, mockStageGroup);

      setTextNoteDialogOpen(false);
      setTextNoteTitle('');
      setTextNoteContent('');
      setTextNoteStage(null);

    } catch (error) {
      console.error("Error saving note:", error);
      showErrorToast("Failed to save note");
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleCloseTextNoteDialog = () => {
    if (isListening) {
      handleStopListening();
    }
    setTextNoteDialogOpen(false);
    setTextNoteTitle('');
    setTextNoteContent('');
    setTextNoteStage(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SchoolIcon sx={{ fontSize: 32, color: '#1976d2' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Learning
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Content curated for team learning - {totalContent} item{totalContent !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        <TextField
          placeholder="Search content, companies..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : learningData.length === 0 && allStages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No stages available
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Please contact admin to set up stages
          </Typography>
        </Box>
      ) : (
        /* Kanban Board - Stage-wise columns */
        <Box sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: 'calc(100vh - 180px)',
          overflow: 'hidden'
        }}>
          <Box sx={{
            backgroundColor: '#f5f5f5',
            p: 2,
            pb: 1,
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            <Box sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              alignItems: 'flex-start',
              height: '100%',
              pb: 2,
              '&::-webkit-scrollbar': {
                height: '8px'
              },
              '&::-webkit-scrollbar-track': {
                background: '#e0e0e0',
                borderRadius: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bdbdbd',
                borderRadius: '10px',
                '&:hover': {
                  background: '#9e9e9e'
                }
              }
            }}>
              {learningData.map((stageGroup, index) => {
                return (

                  <Box key={index} sx={{
                    minWidth: 320,
                    maxWidth: 320,
                    width: 320,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    maxHeight: 'calc(100vh - 230px)'
                  }}>
                    <Card sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      height: '100%',
                      overflow: 'hidden'
                    }}>
                      <CardHeader
                        title={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.95rem',
                                  color: '#1e293b',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                {stageGroup.stage?.name || 'Uncategorized'}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={stageGroup.count || 0}
                              sx={{
                                ml: 1,
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: '#e3f2fd',
                                color: '#1976d2',
                                border: '1px solid #bbdefb',
                                '& .MuiChip-label': {
                                  px: 1.2
                                }
                              }}
                            />
                          </Box>
                        }
                        sx={{
                          pb: 0.5,
                          pt: 1,
                          px: 1.5,
                          bgcolor: '#fff',
                          flexShrink: 0
                        }}
                      />
                      <CardContent sx={{
                        pt: 1,
                        px: 1.5,
                        pb: 1,
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minHeight: 0,
                        '&::-webkit-scrollbar': {
                          width: '6px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '10px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#cbd5e1',
                          borderRadius: '10px',
                          '&:hover': {
                            background: '#94a3b8'
                          }
                        }
                      }}>
                        {stageGroup.videos?.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            <Typography variant="caption">No content in this stage</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {stageGroup.videos?.map((item) => (
                              <LearningContentCard
                                key={item.id}
                                learningItem={item}
                                onEdit={handleEdit}
                                onRemove={handleRemove}
                                onViewInfo={handleViewInfo}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>

                      {(viewMode === 'stage' || viewMode === 'data') && (
                        <Box sx={{
                          p: 1, px: 1.5, borderTop: '1px solid #e0e0e0',
                          opacity: 1
                        }}>
                          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#9e9e9e', mb: 0.5, fontSize: '0.7rem' }}>
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title={
                              "Upload Video"} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  component="label"
                                  sx={{
                                    backgroundColor: '#ffebee',
                                    color: '#d32f2f',
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    '&:hover': { backgroundColor: '#ffcdd2' },
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Video size={18} />
                                  <input
                                    type="file"
                                    hidden
                                    accept="video/*,.mp4,.avi,.mov,.webm,.mkv,.flv,.wmv,.m4a"
                                    onChange={(e) => handleUniversalFileUpload(e, stageGroup)}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title={
                              "Upload Document"} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  component="label"
                                  sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    '&:hover': { backgroundColor: '#bbdefb' },
                                    cursor: 'pointer'
                                  }}
                                >
                                  <FileText size={18} />
                                  <input
                                    type="file"
                                    hidden
                                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv"
                                    onChange={(e) => handleUniversalFileUpload(e, stageGroup)}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {recordingStages[stageGroup.stage.id] ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f44336', animation: 'pulse 1s infinite' }} />
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                    {Math.floor((recordingTimes[stageGroup.stage.id] || 0) / 60)}:{((recordingTimes[stageGroup.stage.id] || 0) % 60).toString().padStart(2, '0')}
                                  </Typography>
                                </Box>
                                <IconButton size="small"
                                  onClick={() => stopRecording(stageGroup)}
                                  sx={{ color: '#f44336' }}>
                                  <Box sx={{ width: 12, height: 12, backgroundColor: '#f44336' }} />
                                </IconButton>
                              </Box>) : (
                              <Tooltip title={
                                "Record Audio"} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => startRecording(stageGroup)}
                                    sx={{
                                      backgroundColor: '#fff3e0',
                                      color: '#f57c00',
                                      width: 36,
                                      height: 36,
                                      borderRadius: 2,
                                      '&:hover': { backgroundColor: '#ffe0b2' },
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Mic size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}


                            <Tooltip title={
                              "Text Note"} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setTextNoteStage(stageGroup);
                                    setTextNoteTitle('');
                                    setTextNoteContent('');
                                    setTextNoteDialogOpen(true);
                                  }}
                                  sx={{
                                    backgroundColor: '#fff3e0',
                                    color: '#f57c00',
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    '&:hover': { backgroundColor: '#ffe0b2' },
                                    cursor: 'pointer'
                                  }}
                                >
                                  <StickyNote size={18} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}














                    </Card>
                  </Box>
                )
              })}
            </Box>          </Box>
        </Box>
      )}

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color: '#1976d2' }} />
          Edit Learning Notes
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Content: <strong>{editingItem?.document?.file_name || 'Content'}</strong>
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Learning Notes"
            placeholder="Explain why this content is important, key takeaways, relevant sections..."
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleSaveEdit}
            variant="contained"
            loading={isSaving}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f' }}>
          Remove from Learning?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to remove <strong>{deletingItem?.document?.file_name || 'this content'}</strong> from Learning?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Note: This will only remove it from the Learning section. The original file will NOT be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={confirmRemove}
            variant="contained"
            color="error"
            loading={isDeleting}
          >
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: '#1976d2' }} />
          Learning Notes
        </DialogTitle>
        <DialogContent>
          {viewingItem && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Content: <strong>{viewingItem.document?.file_name || 'Content'}</strong>
              </Typography>

              <Box sx={{
                p: 2,
                bgcolor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {viewingItem.notes || 'No notes added'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#1976d2' }}
                >
                  {viewingItem.added_by?.first_name?.[0] || 'U'}
                </Avatar>
                <Typography variant="caption" color="text.secondary">
                  Added by {viewingItem.added_by?.first_name} {viewingItem.added_by?.last_name} on {getDDMMYYYYFormat(viewingItem.created)}
                </Typography>
              </Box>

              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Company:</strong> {viewingItem.company?.company_name || 'Unknown'}
                  {viewingItem.stage && (
                    <> | <strong>Stage:</strong> {viewingItem.stage.name}</>
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <TextNoteDialog
        open={textNoteDialogOpen}
        onClose={handleCloseTextNoteDialog}
        noteTitle={textNoteTitle}
        onTitleChange={setTextNoteTitle}
        noteContent={textNoteContent}
        onContentChange={setTextNoteContent}
        onSave={handleSaveTextNote}
        isListening={isListening}
        onStartListening={handleStartListening}
        onStopListening={handleStopListening}
        isSaving={isSavingNote}
        stageName={textNoteStage?.stage?.name || ''}
        isEditing={false}
      />
    </Box>
  );
}
